import { Injectable, Injector, inject } from '@angular/core';
import {
  Analytics,
  isSupported,
  logEvent,
  setAnalyticsCollectionEnabled,
  setUserId,
  setUserProperties,
} from '@angular/fire/analytics';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class AnalyticsService {
  private injector = inject(Injector);
  private _analytics: Analytics | null = null;
  private _initialized = false;

  /**
   * Lazily resolves the Analytics instance after confirming browser support.
   * Per @angular/fire docs, Analytics must NOT be injected synchronously in
   * a constructor — use `injector.get(Analytics)` after `await isSupported()`.
   */
  private async getAnalytics(): Promise<Analytics | null> {
    if (this._initialized) return this._analytics;
    this._initialized = true;

    const supported = await isSupported();
    if (!supported) return null;

    this._analytics = this.injector.get(Analytics);
    // Suppress all collection in non-production environments so dev traffic
    // never pollutes Firebase Analytics data.
    setAnalyticsCollectionEnabled(this._analytics, environment.production);
    return this._analytics;
  }

  // ─── User Identity ────────────────────────────────────────────────────────

  async setUserId(uid: string | null): Promise<void> {
    const analytics = await this.getAnalytics();
    if (!analytics) return;
    setUserId(analytics, uid);
  }

  async setUserProperties(properties: {
    city?: string;
    country?: string;
    nationality?: string;
    [key: string]: string | undefined;
  }): Promise<void> {
    const analytics = await this.getAnalytics();
    if (!analytics) return;
    setUserProperties(analytics, properties);
  }

  // ─── Auth Events ──────────────────────────────────────────────────────────

  async logLogin(method: 'email' | 'google' | 'apple' | string): Promise<void> {
    const analytics = await this.getAnalytics();
    if (!analytics) return;
    logEvent(analytics, 'login', { method });
  }

  async logSignUp(method: 'email' | 'google' | 'apple' | string): Promise<void> {
    const analytics = await this.getAnalytics();
    if (!analytics) return;
    logEvent(analytics, 'sign_up', { method });
  }

  // ─── Community / Group Events ─────────────────────────────────────────────

  async logGroupJoin(params: { group_id: string; group_name?: string }): Promise<void> {
    const analytics = await this.getAnalytics();
    if (!analytics) return;
    logEvent(analytics, 'join_group', params);
  }

  async logGroupView(params: { group_id: string; group_name?: string }): Promise<void> {
    const analytics = await this.getAnalytics();
    if (!analytics) return;
    logEvent(analytics, 'view_item', { item_category: 'group', ...params });
  }

  // ─── Content Events ───────────────────────────────────────────────────────

  async logVideoWatch(params: {
    video_id: string;
    video_title?: string;
    channel_id?: string;
  }): Promise<void> {
    const analytics = await this.getAnalytics();
    if (!analytics) return;
    logEvent(analytics, 'watch_video', params);
  }

  async logPodcastListen(params: {
    podcast_id: string;
    podcast_title?: string;
    channel_id?: string;
  }): Promise<void> {
    const analytics = await this.getAnalytics();
    if (!analytics) return;
    logEvent(analytics, 'listen_podcast', params);
  }

  async logEventView(params: {
    event_id: string;
    event_name?: string;
    group_id?: string;
  }): Promise<void> {
    const analytics = await this.getAnalytics();
    if (!analytics) return;
    logEvent(analytics, 'view_item', { item_category: 'event', ...params });
  }

  async logChannelView(params: {
    channel_id: string;
    channel_name?: string;
  }): Promise<void> {
    const analytics = await this.getAnalytics();
    if (!analytics) return;
    logEvent(analytics, 'view_item', { item_category: 'channel', ...params });
  }

  // ─── Quiz Events ──────────────────────────────────────────────────────────

  async logQuizStart(params: {
    quiz_id: string;
    quiz_title?: string;
    group_id?: string;
  }): Promise<void> {
    const analytics = await this.getAnalytics();
    if (!analytics) return;
    logEvent(analytics, 'quiz_start', params);
  }

  async logQuizComplete(params: {
    quiz_id: string;
    quiz_title?: string;
    score?: number;
    group_id?: string;
  }): Promise<void> {
    const analytics = await this.getAnalytics();
    if (!analytics) return;
    logEvent(analytics, 'quiz_complete', params);
  }

  // ─── Search Events ────────────────────────────────────────────────────────

  async logSearch(params: { search_term: string; content_type?: string }): Promise<void> {
    const analytics = await this.getAnalytics();
    if (!analytics) return;
    logEvent(analytics, 'search', params);
  }

  // ─── Prayer Events ────────────────────────────────────────────────────────

  async logPrayerCreate(params: {
    group_id: string;
    category?: string;
  }): Promise<void> {
    const analytics = await this.getAnalytics();
    if (!analytics) return;
    logEvent(analytics, 'create_prayer', params);
  }

  // ─── Generic ──────────────────────────────────────────────────────────────

  async logCustomEvent(
    name: string,
    params?: Record<string, string | number | boolean>,
  ): Promise<void> {
    const analytics = await this.getAnalytics();
    if (!analytics) return;
    logEvent(analytics, name, params);
  }
}
